import { Controller } from "@hotwired/stimulus";
import { application } from "src/stimulus/application";
import { escapeHtml, truncateTitle, utilizationLabelsHtml, NO_IMAGE_PATH } from "src/site/sample_card";

// Site::PrefectureArea::ExperienceSection::Component の Stimulus コントローラ。
// landIeul: ExperienceSection の useQuery(GetExperiencesByPrefectureAndKind) をクライアント fetch で再現し、
// Home::Samples::Experiences と同じ Experience_* カードを組む。0 件なら section を出さない (hidden のまま)。
const QUERY = `query GetExperiencesByPrefectureAndKind($prefectureId: ID!, $kind: ID!, $limit: Int){
  experiencesByPrefectureAndKind(prefectureId: $prefectureId, kind: $kind, limit: $limit){
    id title age cityAddress landArea landUtilizationMethods firstImage { thumbnailUrl } prefecture { name }
  }
}`;

interface ExperienceData {
  id: string;
  title: string;
  age: string;
  cityAddress: string;
  landArea?: string | null;
  landUtilizationMethods: string[];
  firstImage?: { thumbnailUrl?: string } | null;
  prefecture: { name: string };
}

class SitePrefectureAreaExperienceSectionController extends Controller<HTMLElement> {
  static targets = ["wrapper"];
  static values = { graphql: String, prefectureId: String, kind: String, limit: Number };

  declare readonly wrapperTarget: HTMLElement;
  declare readonly graphqlValue: string;
  declare readonly prefectureIdValue: string;
  declare readonly kindValue: string;
  declare readonly limitValue: number;

  connect(): void {
    void this.load();
  }

  private async load(): Promise<void> {
    const res = await fetch(this.graphqlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        query: QUERY,
        variables: {
          prefectureId: this.prefectureIdValue,
          kind: this.kindValue,
          limit: this.limitValue,
        },
      }),
    });
    const json = await res.json();
    const experiences: ExperienceData[] = json?.data?.experiencesByPrefectureAndKind || [];
    if (experiences.length === 0) return;

    this.wrapperTarget.innerHTML = experiences.map((e, i) => this.cardHtml(e, i)).join("");
    this.element.hidden = false;
  }

  private cardHtml(experience: ExperienceData, index: number): string {
    const imageUrl = experience.firstImage?.thumbnailUrl || NO_IMAGE_PATH;
    const alt = `${experience.title} の施工イメージ${index + 1}`;
    return `<a href="/case/${escapeHtml(experience.id)}/" class="Experience_card">
  <div class="Experience_inner">
    <div class="Experience_image">
      <img alt="${escapeHtml(alt)}" loading="lazy" decoding="async" sizes="100vw" src="${escapeHtml(imageUrl)}" style="position:absolute;height:100%;width:100%;left:0;top:0;right:0;bottom:0;object-fit:cover;color:transparent">
    </div>
    <h3 class="Experience_title">${escapeHtml(truncateTitle(experience.title))}</h3>
    <div class="Experience_content">
      <div class="Experience_row">
        <p class="Experience_utilizationColumn">活用法：${utilizationLabelsHtml(experience.landUtilizationMethods)}</p>
      </div>
      <div class="Experience_row">
        <p>年齢：${escapeHtml(String(experience.age ?? ""))}歳</p>
      </div>
      <div class="Experience_row">
        <p>住所：${escapeHtml(experience.prefecture.name)}</p>
      </div>
      <div class="Experience_row">
        <p>立地の広さ：${escapeHtml(experience.landArea ?? "")}</p>
      </div>
    </div>
  </div>
</a>`;
  }
}

application.register(
  "site--prefecture-area--experience-section",
  SitePrefectureAreaExperienceSectionController,
);
